import { FunctionComponent, forwardRef, PropsWithoutRef, ReactNode, Ref } from 'react';
import styled, { css } from 'styled-components';
import { rgba } from 'polished';

import { Flex, tryCatch, defaultThemeProp, ForwardProps, PropsWithDefaults } from '@pega/cosmos-react-core';
import React from 'react';

export interface SystemMessageProps {
  /** Ref to the container element */
  ref?: Ref<HTMLDivElement>;
  /** System message to be displayed */
  message: ReactNode;
  /** timestamp of the message(Formatted) */
  timeStamp?: string;
  /** variant of this system message
   * @default "secondary"
   */
  variant?: 'primary' | 'secondary';
}

type SystemMessagePropsWithDefaults = PropsWithDefaults<SystemMessageProps, 'variant'>;

export const StyledSystemMessage = styled.div<Pick<SystemMessagePropsWithDefaults, 'variant'>>(
  ({
    theme: {
      base: {
        palette: { 'foreground-color': foregroundColor },
        transparency: { 'transparent-3': foregroundAlpha },
      },
      components: {
        chat: {
          messageBubble: {
            sender: {
              content: { 'background-color': primarySystemMessageColor },
            },
          },
        },
      },
    },
    variant,
  }) => {
    const systemMessageForeGroundColor =
      variant === 'primary' ? primarySystemMessageColor : tryCatch(() => rgba(foregroundColor, foregroundAlpha));
    return css`
      color: ${systemMessageForeGroundColor};
      font-size: 0.75rem;
      padding: 0 0 0.5rem;
      ::before,
      ::after {
        display: inline-flex;
        content: '';
        border-top: 0.0625rem solid ${systemMessageForeGroundColor};
        flex-grow: 1;
        min-width: 1.5rem;
      }
      ::before {
        margin: 0 0.5rem 0 0;
      }
      ::after {
        margin: 0 0 0 0.5rem;
      }
    `;
  },
);

StyledSystemMessage.defaultProps = defaultThemeProp;

const SystemMessage: FunctionComponent<SystemMessageProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<SystemMessageProps>, ref: SystemMessageProps['ref']) => {
    const { message, timeStamp, variant = 'secondary', ...restProps } = props;
    return (
      <Flex
        ref={ref}
        as={StyledSystemMessage}
        container={{
          alignItems: 'center',
        }}
        item={{
          shrink: 0,
        }}
        variant={variant}
        {...restProps}
      >
        {timeStamp && `${timeStamp}: `} {message}
      </Flex>
    );
  },
);

export default SystemMessage;
